import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Sparkles, MessageSquare } from "lucide-react";

const A = "https://customer-assets-lqy194kg.emergentagent.net/job_web-transform-57/artifacts/";

// Curated live demo sites (each with its own AI Assistant) to feature on
// the India / Kerala homepages.
const FEATURED = [
  { title: "Solara Cove Resort", vertical: "Resorts & Hotels", assistant: "Sola", accent: "#1E6F78", design: A + "1fp9q4j9_8-resort-w-reservation.png", route: "/demos/hotel" },
  { title: "Aurelia", vertical: "Restaurants", assistant: "Aria", accent: "#C9A24B", design: A + "mamsac10_sit-down-restaurant.png", route: "/demos/restaurant" },
  { title: "Harborlight Medical", vertical: "Clinics & Healthcare", assistant: "Clara", accent: "#2E7DAF", design: A + "l2nizn4v_medical-office.png", route: "/demos/medical" },
  { title: "Maya Bennett Realty", vertical: "Realtors & Builders", assistant: "Haven", accent: "#2F5D50", design: A + "988fele5_real-estate-broker.png", route: "/demos/real-estate" },
  { title: "Cedar Grove College", vertical: "Schools & Colleges", assistant: "Grove", accent: "#2E6B4F", design: A + "16aw2g20_9-educaton-college.png", route: "/demos/college" },
  { title: "Fresh Market", vertical: "Shops & Retail", assistant: "Basil", accent: "#2F8F3E", design: A + "pcysiflb_15-grocery-store-home.png", route: "/demos/fresh" },
];

/**
 * Reusable "Live Demos" showcase strip for the India / Kerala homepages.
 * Props: accent (hex), eyebrow, title, subtitle.
 */
export const LiveDemosSection = ({ accent = "#024E3F", eyebrow = "See it live", title = "Live demo sites, each with its own AI Assistant", subtitle = "Real, browsable websites we've built for hotels, restaurants, clinics, shops and more — every one with a smart AI Assistant that answers questions and captures bookings." }) => {
  return (
    <section data-testid="live-demos-section" className="py-16 md:py-20" style={{ background: `${accent}0A` }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em]" style={{ color: accent }}><Sparkles size={13} /> {eyebrow}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mt-3">{title}</h2>
            <p className="text-slate-500 mt-3 text-base">{subtitle}</p>
          </div>
          <Link to="/demos" data-testid="live-demos-view-all" className="inline-flex items-center gap-2 font-semibold rounded-xl px-5 py-3 text-white whitespace-nowrap hover:-translate-y-0.5 transition-transform" style={{ background: accent }}>
            View all demos <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {FEATURED.map((d, i) => (
            <motion.div key={d.route}
              initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.06, duration: 0.5 }}>
              <Link to={d.route} data-testid={`live-demo-card-${d.route.split("/").pop()}`}
                className="group block rounded-2xl overflow-hidden bg-white border border-slate-200 hover:border-slate-300 card-shadow transition-all hover:-translate-y-1">
                <div className="relative h-44 overflow-hidden bg-slate-100">
                  <img src={d.design} alt={`${d.title} website`} loading="lazy"
                    className="w-full h-full object-cover object-top transition-[object-position] duration-[2500ms] ease-linear group-hover:object-bottom" />
                  <span className="absolute top-3 left-3 text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full text-white" style={{ background: d.accent }}>{d.vertical}</span>
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded-full bg-emerald-400/90 text-emerald-950">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-950 animate-pulse" /> Live
                  </span>
                </div>
                <div className="p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-bold text-slate-900 truncate">{d.title}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Sparkles size={11} style={{ color: d.accent }} /> AI Assistant: {d.assistant}</div>
                  </div>
                  <ArrowUpRight size={18} className="text-slate-400 group-hover:text-slate-900 transition-colors shrink-0" />
                </div>
              </Link>
              <button
                onClick={() => window.open(`${d.route}?ai=1`, "_blank", "noopener")}
                data-testid={`live-demo-ask-${d.route.split("/").pop()}`}
                className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold rounded-lg px-3 py-2.5 transition-colors"
                style={{ background: `${d.accent}12`, color: d.accent }}>
                <MessageSquare size={13} /> Ask the AI Assistant
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
